/**
 * Forge roles, highest first: owner > admin > user. The owner (Justin) can do
 * everything an admin can, so every "is this an admin" question in the UI is
 * answered here and is true for both. /auth/user already reports `is_admin`
 * true for an owner; the role string is checked too so a list row from
 * /admin/users (which carries only `role`) gets the same answer.
 */

export type Role = "owner" | "admin" | "user"

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  user: "User",
}

interface RoleBearer {
  role?: string | null
  is_admin?: boolean
  is_owner?: boolean
}

export function isOwner(user: RoleBearer | null | undefined): boolean {
  return !!user && (user.is_owner === true || user.role === "owner")
}

export function isAdmin(user: RoleBearer | null | undefined): boolean {
  return !!user && (user.is_admin === true || user.role === "admin" || isOwner(user))
}

/**
 * Events: the owner, or anyone with the per-person toggle. Admins no longer
 * get it by role. /auth/user reports the answer as `events_enabled`.
 */
export function hasEventsAccess(user: (RoleBearer & { events_enabled?: boolean }) | null | undefined): boolean {
  return isOwner(user) || user?.events_enabled === true
}

/** The label for a role string, falling back to the raw value for anything unknown. */
export function roleLabel(role: string | null | undefined): string {
  if (!role) return ROLE_LABELS.user
  return ROLE_LABELS[role as Role] ?? role
}

/**
 * What an admin page may offer to change on one user's row. Mirrors the
 * refusals in AdminUserController: only an owner may grant or remove `owner`
 * or deactivate an owner, and the last owner cannot step down.
 */
export function ownerRowLocks(
  actor: RoleBearer | null | undefined,
  row: RoleBearer,
  ownerCount: number,
): { role: string | null; deactivate: string | null } {
  if (!isOwner(row)) return { role: null, deactivate: null }
  if (!isOwner(actor)) {
    return {
      role: "Only an owner can change an owner's role.",
      deactivate: "Only an owner can deactivate an owner.",
    }
  }
  return {
    role: ownerCount <= 1 ? "Forge needs an owner: make someone else owner before stepping down." : null,
    deactivate: null,
  }
}
